import { useId, useLayoutEffect, useState, type RefObject } from 'react'

/**
 * "Said once per page, by the first in reading order": the one mechanism behind
 * the error notes' reassurance (`components/errors/reassurance.ts`, Spec 2 D5,
 * #212) and the busy line (`components/ui/useBusyLine.ts`, #392). Each is a *claim*
 * made by a number of elements on the page that only one of them may act on, and
 * the claimant has to be the one a reader meets first.
 *
 * `createFirstOnPage()` makes one such scope. The scopes are independent: the
 * note that says the reassurance is not the loading panel that says "busy".
 *
 * Module state rather than a context, on purpose: the app mounts one page at a
 * time, so the document is the page, and every element on it is in one scope
 * with no provider to forget. An element leaving (its panel recovered, the page
 * changed) hands the claim to whichever is now first; an element appearing above
 * the claimant takes it over.
 *
 * Two limits, both true of the app today and worth knowing before either
 * changes. An element mounted outside the page (in the app shell, beside the
 * sidebar or the coach dock) would compete for the page's claim; none does. And
 * the order is worked out when an element arrives or leaves, not when elements
 * move, because nothing on these pages moves one without remounting it.
 *
 * The hook registers in a layout effect, so the element that should act does so
 * before the first paint rather than a frame later. State, subscribed inside
 * that layout effect, rather than `useSyncExternalStore`: the store only ever
 * changes in the commit, and `useSyncExternalStore` notices a change made during
 * the commit only in its own passive effect, so "before the first paint" would
 * hold only when the scheduler happened to flush that effect before yielding
 * (the frame tests in `PanelError.test.tsx`, and the Journey hero test that
 * flaked on CI run 93). A `setState` inside a layout effect is processed before
 * the paint by React's own contract, for this element and every one it notifies.
 * The tearing protection given up is for stores that change during render, which
 * this one never does.
 */
export function createFirstOnPage(): (ref: RefObject<HTMLElement | null>) => boolean {
  const onPage = new Map<string, HTMLElement>()
  const listeners = new Set<() => void>()
  let first: string | null = null

  function findFirst() {
    let found: { id: string; element: HTMLElement } | null = null
    for (const [id, element] of onPage) {
      if (!element.isConnected) continue
      if (found === null || found.element.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING) {
        found = { id, element }
      }
    }
    const id = found?.id ?? null
    if (id !== first) {
      first = id
      for (const listener of listeners) listener()
    }
  }

  function subscribe(listener: () => void) {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  const read = () => first

  /** Is the element at `ref` the first of its kind on the page, in reading order? */
  return function useIsFirstOnPage(ref) {
    const id = useId()
    const [current, setCurrent] = useState(read)
    useLayoutEffect(() => {
      const element = ref.current
      if (!element) return
      const sync = () => setCurrent(read())
      const unsubscribe = subscribe(sync)
      onPage.set(id, element)
      findFirst()
      sync()
      return () => {
        unsubscribe()
        onPage.delete(id)
        findFirst()
      }
    }, [id, ref])
    return current === id
  }
}
